import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  onSnapshot,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import { db, auth } from '../firebase';

// Helper to get current user ID
const getUserId = () => auth.currentUser?.uid;

class ChatApi {
  // Chat Rooms
  async getRooms() {
    try {
      const userId = getUserId();
      const q = query(
        collection(db, 'chatRooms'),
        where('participants', 'array-contains', userId),
        orderBy('updatedAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (error) {
      console.error('Error fetching chat rooms:', error);
      throw error;
    }
  }

  async createRoom(roomData) {
    try {
      const userId = getUserId();
      const ref = await addDoc(collection(db, 'chatRooms'), {
        ...roomData,
        createdBy: userId,
        participants: roomData.participants || [userId],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return { id: ref.id, ...roomData };
    } catch (error) {
      console.error('Error creating chat room:', error);
      throw error;
    }
  }

  async joinRoom(roomId) {
    try {
      const userId = getUserId();
      await updateDoc(doc(db, 'chatRooms', roomId), {
        participants: arrayUnion(userId),
        updatedAt: serverTimestamp(),
      });
      return { success: true };
    } catch (error) {
      console.error('Error joining chat room:', error);
      throw error;
    }
  }

  async leaveRoom(roomId) {
    try {
      const userId = getUserId();
      await updateDoc(doc(db, 'chatRooms', roomId), {
        participants: arrayRemove(userId),
        updatedAt: serverTimestamp(),
      });
      return { success: true };
    } catch (error) {
      console.error('Error leaving chat room:', error);
      throw error;
    }
  }

  async getRoomDetails(roomId) {
    try {
      const snap = await getDoc(doc(db, 'chatRooms', roomId));
      if (!snap.exists()) return null;
      return { id: snap.id, ...snap.data() };
    } catch (error) {
      console.error('Error fetching room details:', error);
      throw error;
    }
  }

  async updateRoom(roomId, updateData) {
    try {
      await updateDoc(doc(db, 'chatRooms', roomId), {
        ...updateData,
        updatedAt: serverTimestamp(),
      });
      return { id: roomId, ...updateData };
    } catch (error) {
      console.error('Error updating room:', error);
      throw error;
    }
  }

  async deleteRoom(roomId) {
    try {
      await deleteDoc(doc(db, 'chatRooms', roomId));
      return { success: true };
    } catch (error) {
      console.error('Error deleting room:', error);
      throw error;
    }
  }

  // Messages
  async getMessages(roomId, msgLimit = 50, offset = 0) {
    try {
      const q = query(
        collection(db, 'chatRooms', roomId, 'messages'),
        orderBy('createdAt', 'desc'),
        firestoreLimit(msgLimit)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map((d) => ({ id: d.id, ...d.data() })).reverse();
    } catch (error) {
      console.error('Error fetching messages:', error);
      throw error;
    }
  }

  async sendMessage(roomId, messageData) {
    try {
      const userId = getUserId();
      const ref = await addDoc(collection(db, 'chatRooms', roomId, 'messages'), {
        ...messageData,
        senderId: userId,
        createdAt: serverTimestamp(),
      });
      // Update room's last activity
      await updateDoc(doc(db, 'chatRooms', roomId), {
        lastMessage: messageData.content || '',
        updatedAt: serverTimestamp(),
      });
      return { id: ref.id, ...messageData, senderId: userId };
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }

  async editMessage(messageId, content) {
    try {
      // messageId format: "roomId/messageId" or we search across rooms
      // For simplicity, expect callers to also pass roomId or use a flat messages collection
      // We'll store a reference; callers should provide roomId via a wrapper
      console.warn('editMessage requires roomId context. Use updateDoc directly with room path.');
      return { id: messageId, content };
    } catch (error) {
      console.error('Error editing message:', error);
      throw error;
    }
  }

  async deleteMessage(messageId) {
    try {
      console.warn('deleteMessage requires roomId context. Use deleteDoc directly with room path.');
      return { success: true };
    } catch (error) {
      console.error('Error deleting message:', error);
      throw error;
    }
  }

  async reactToMessage(messageId, emoji) {
    try {
      console.warn('reactToMessage requires roomId context.');
      return { id: messageId, emoji };
    } catch (error) {
      console.error('Error reacting to message:', error);
      throw error;
    }
  }

  async markMessageAsRead(messageId) {
    try {
      // No-op in Firestore approach; read status can be tracked via a separate subcollection
      return { success: true };
    } catch (error) {
      console.error('Error marking message as read:', error);
      throw error;
    }
  }

  // Real-time listener for messages in a room
  onMessages(roomId, callback) {
    const q = query(
      collection(db, 'chatRooms', roomId, 'messages'),
      orderBy('createdAt', 'asc')
    );
    return onSnapshot(q, (snapshot) => {
      const messages = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      callback(messages);
    });
  }

  // File uploads - placeholder (use Firebase Storage in production)
  async uploadFile(roomId, file, messageData = {}) {
    try {
      // In a full implementation, upload to Firebase Storage first, then save URL
      console.warn('File upload requires Firebase Storage integration.');
      return await this.sendMessage(roomId, {
        ...messageData,
        type: 'file',
        fileName: file.name,
        fileSize: file.size,
      });
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  }

  // Push notifications - stored in Firestore
  async registerPushToken(token, deviceType = 'web') {
    try {
      const userId = getUserId();
      const ref = await addDoc(collection(db, 'pushTokens'), {
        userId,
        token,
        deviceType,
        createdAt: serverTimestamp(),
      });
      return { id: ref.id, token, deviceType };
    } catch (error) {
      console.error('Error registering push token:', error);
      throw error;
    }
  }

  async unregisterPushToken(token) {
    try {
      const userId = getUserId();
      const q = query(
        collection(db, 'pushTokens'),
        where('userId', '==', userId),
        where('token', '==', token)
      );
      const snapshot = await getDocs(q);
      const deletePromises = snapshot.docs.map((d) => deleteDoc(d.ref));
      await Promise.all(deletePromises);
      return { success: true };
    } catch (error) {
      console.error('Error unregistering push token:', error);
      throw error;
    }
  }

  // Notification preferences
  async getNotificationPreferences() {
    try {
      const userId = getUserId();
      const snap = await getDoc(doc(db, 'notificationPreferences', userId));
      if (!snap.exists()) return {};
      return { id: snap.id, ...snap.data() };
    } catch (error) {
      console.error('Error fetching notification preferences:', error);
      throw error;
    }
  }

  async updateNotificationPreferences(preferences) {
    try {
      const userId = getUserId();
      const docRef = doc(db, 'notificationPreferences', userId);
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        await updateDoc(docRef, { ...preferences, updatedAt: serverTimestamp() });
      } else {
        // Use setDoc for creating with a specific ID
        const { setDoc } = await import('firebase/firestore');
        await setDoc(docRef, { ...preferences, userId, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
      }
      return preferences;
    } catch (error) {
      console.error('Error updating notification preferences:', error);
      throw error;
    }
  }

  // WebSocket replacement - use onMessages for real-time instead
  createWebSocketConnection(roomId, token) {
    // Return an unsubscribe function from onSnapshot instead
    console.warn('WebSocket replaced by Firestore onSnapshot. Use chatApi.onMessages() instead.');
    return null;
  }

  // Search functionality
  async searchMessages(searchQuery, roomId = null, msgLimit = 20) {
    try {
      // Firestore does not support full-text search natively.
      // For production, use Algolia or Elasticsearch. This is a basic prefix search.
      if (!roomId) {
        console.warn('Search without roomId is not supported in Firestore. Provide a roomId.');
        return [];
      }
      const q = query(
        collection(db, 'chatRooms', roomId, 'messages'),
        orderBy('createdAt', 'desc'),
        firestoreLimit(msgLimit)
      );
      const snapshot = await getDocs(q);
      const messages = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      // Client-side filter
      return messages.filter(
        (m) => m.content && m.content.toLowerCase().includes(searchQuery.toLowerCase())
      );
    } catch (error) {
      console.error('Error searching messages:', error);
      throw error;
    }
  }

  // Analytics
  async getChatAnalytics(startDate, endDate) {
    try {
      const userId = getUserId();
      const q = query(
        collection(db, 'chatAnalytics'),
        where('userId', '==', userId)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (error) {
      console.error('Error fetching chat analytics:', error);
      throw error;
    }
  }

  // User management
  async getRoomParticipants(roomId) {
    try {
      const snap = await getDoc(doc(db, 'chatRooms', roomId));
      if (!snap.exists()) return [];
      return snap.data().participants || [];
    } catch (error) {
      console.error('Error fetching room participants:', error);
      throw error;
    }
  }

  async addParticipants(roomId, userIds) {
    try {
      await updateDoc(doc(db, 'chatRooms', roomId), {
        participants: arrayUnion(...userIds),
        updatedAt: serverTimestamp(),
      });
      return { success: true };
    } catch (error) {
      console.error('Error adding participants:', error);
      throw error;
    }
  }

  async removeParticipant(roomId, userId) {
    try {
      await updateDoc(doc(db, 'chatRooms', roomId), {
        participants: arrayRemove(userId),
        updatedAt: serverTimestamp(),
      });
      return { success: true };
    } catch (error) {
      console.error('Error removing participant:', error);
      throw error;
    }
  }

  // Vendor notifications
  async createVendorNotification(vendorId, triggerType, settings) {
    try {
      const userId = getUserId();
      const ref = await addDoc(collection(db, 'vendorNotifications'), {
        vendorId,
        triggerType,
        ...settings,
        userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return { id: ref.id, vendorId, triggerType, ...settings };
    } catch (error) {
      console.error('Error creating vendor notification:', error);
      throw error;
    }
  }

  async getVendorNotifications(vendorId) {
    try {
      const q = query(
        collection(db, 'vendorNotifications'),
        where('vendorId', '==', vendorId)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (error) {
      console.error('Error fetching vendor notifications:', error);
      throw error;
    }
  }

  async updateVendorNotification(notificationId, settings) {
    try {
      await updateDoc(doc(db, 'vendorNotifications', notificationId), {
        ...settings,
        updatedAt: serverTimestamp(),
      });
      return { id: notificationId, ...settings };
    } catch (error) {
      console.error('Error updating vendor notification:', error);
      throw error;
    }
  }

  async deleteVendorNotification(notificationId) {
    try {
      await deleteDoc(doc(db, 'vendorNotifications', notificationId));
      return { success: true };
    } catch (error) {
      console.error('Error deleting vendor notification:', error);
      throw error;
    }
  }
}

// Create and export a singleton instance
const chatApi = new ChatApi();
export default chatApi;
